import { Route } from "@angular/router";

/**
 * Child routes for the Rules Authoring feature shell.
 *
 * Loaded lazily under the `/rules` parent path declared in `app.routes.ts`.
 * The default child route renders the rules browser (F2 — `RulesBrowserComponent`).
 *
 * Editor routes (F3a):
 * - `new` — create mode (`/rules/new`).
 * - `:family/:ruleKey/edit` — edit mode for an existing DeedCore draft rule.
 *
 * Batch lifecycle routes (FR-6 FL1 / FL2):
 * - `batches/new` — create a new staged batch.
 * - `batches/:id` — batch workspace for staging, reviewing, and navigating a batch.
 * - `batches/:id/diff` — field-level staged-vs-live diff view for a batch (FL2).
 *
 * IMPORTANT: `batches/new` is declared before `batches/:id` so the literal
 * `new` segment is not captured as a dynamic `:id` param.
 */
export const rulesAuthoringRoutes: Route[] = [
  {
    path: "",
    loadComponent: () =>
      import("./rules-browser.component").then((m) => m.RulesBrowserComponent),
    data: { title: "Rules" },
  },
  {
    path: "test",
    loadComponent: () =>
      import("./test-order/test-order.component").then(
        (m) => m.TestOrderComponent,
      ),
    data: { title: "Test an order" },
  },
  {
    path: "new",
    loadComponent: () =>
      import("./rule-editor/rule-editor.component").then(
        (m) => m.RuleEditorComponent,
      ),
    data: { title: "New Rule" },
  },
  {
    path: "batches/new",
    loadComponent: () =>
      import("./batch-workspace/create-batch.component").then(
        (m) => m.CreateBatchComponent,
      ),
    data: { title: "New Batch" },
  },
  {
    path: "batches/:id",
    loadComponent: () =>
      import("./batch-workspace/batch-workspace.component").then(
        (m) => m.BatchWorkspaceComponent,
      ),
    data: { title: "Batch Workspace" },
  },
  {
    path: "batches/:id/diff",
    loadComponent: () =>
      import("./batch-workspace/diff-view.component").then(
        (m) => m.DiffViewComponent,
      ),
    data: { title: "Batch Diff" },
  },
  {
    path: "batches/:id/events",
    loadComponent: () =>
      import("./batch-workspace/rule-events-timeline.component").then(
        (m) => m.RuleEventsTimelineComponent,
      ),
    data: { title: "Batch History" },
  },
  {
    path: "batches/:id/test",
    loadComponent: () =>
      import("./batch-workspace/batch-test-order.component").then(
        (m) => m.BatchTestOrderComponent,
      ),
    data: { title: "Test Order" },
  },
  {
    path: "keys/:ruleKey/events",
    loadComponent: () =>
      import("./batch-workspace/rule-events-timeline.component").then(
        (m) => m.RuleEventsTimelineComponent,
      ),
    data: { title: "Rule History" },
  },
  {
    path: ":family/:ruleKey/edit",
    loadComponent: () =>
      import("./rule-editor/rule-editor.component").then(
        (m) => m.RuleEditorComponent,
      ),
    data: { title: "Edit Rule" },
  },
];
